'use client';

import React, {
  type ForwardRefRenderFunction,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Drawer } from 'antd';
import dayjs from 'dayjs';
import CTag from '../../components/ui/CTag';
import { useBusinessContext } from '../../context/BusinessContext';
import { Collection, Site } from '../../dto';

export type CollectionReportViewModalInstance = {
  view: (report: any) => void;
};

type Props = {};

const CollectionReportViewModal: ForwardRefRenderFunction<
  CollectionReportViewModalInstance,
  Props
> = (_, ref) => {
  const [report, setReport] = useState<Collection>();
  const { collectionType, sites, states } = useBusinessContext();

  useImperativeHandle(
    ref,
    () => ({
      view: (r: any) => {
        setReport(r);
      },
    }),
    [],
  );

  return (
    <Drawer
      width={600}
      destroyOnClose
      title={
        <span className="text-xl font-bold text-gray-800 flex items-center">
          Collection Report
        </span>
      }
      open={!!report}
      onClose={() => setReport(undefined)}
      footer={null}
      className="collection-report-drawer"
      bodyStyle={{ padding: '0' }}
    >
      {report && (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full">
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
            <div className="grid grid-cols-1 gap-2">
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">
                  Store Name:
                </span>
                <span className="text-gray-800">
                  {sites[report.siteId].name || 'N/A'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">State:</span>
                <span className="text-gray-800">
                  {sites[report.siteId]?.state || 'N/A'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">Zone:</span>
                <span className="text-gray-800">
                  {sites[report.siteId].zone || 'N/A'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">Council:</span>
                <span className="text-gray-800">
                  {sites[report.siteId].council || 'N/A'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">Suburb:</span>
                <span className="text-gray-800">
                  {sites[report.siteId].suburb || 'N/A'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">Area:</span>
                <span className="text-gray-800">
                  {sites[report.siteId].area || 'N/A'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">
                  Cost Center:
                </span>
                <span className="text-gray-800">
                  {sites[report.siteId].costCenter || 'N/A'}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">
                  Date & Time:
                </span>
                <span className="text-gray-800">
                  {dayjs(report.createdAt).format('D of MMM, YYYY - h:mmA')}
                </span>
              </div>
              <div className="flex">
                <span className="text-gray-600 font-medium w-28">
                  Created User:
                </span>
                <span className="text-gray-800">
                  {report.createdByName ?? 'Unknown User'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-3">
              Collections
            </h3>
            {report.collections && report.collections.length > 0 ? (
              <div className="space-y-4">
                {report.collections.map((collection: any, index: number) => (
                  <div
                    key={index}
                    className="p-4 bg-gray-50 rounded-md border border-gray-200"
                  >
                    <div className="grid grid-cols-1 gap-2">
                      <div className="flex">
                        <span className="text-gray-600 font-medium w-28">
                          Collection Type:
                        </span>
                        <span className="text-gray-800">
                          {collectionType[collection.collectionType]?.name ||
                            'N/A'}
                        </span>
                      </div>
                      <div className="flex">
                        <span className="text-gray-600 font-medium w-28">
                          Quantity:
                        </span>
                        <span className="text-gray-800">
                          {collection.quantity || 0}
                        </span>
                      </div>
                      <div className="flex">
                        <span className="text-gray-600 font-medium w-28">
                          Weight:
                        </span>
                        <span className="text-gray-800">
                          {collection.weight || 0} kg
                        </span>
                      </div>
                      <div className="flex">
                        <span className="text-gray-600 font-medium w-28">
                          Note:
                        </span>
                        <span className="text-gray-800">
                          {collection.note || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">
                No collection data available
              </p>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export const useCollectionReportViewModal = () =>
  useRef<CollectionReportViewModalInstance>(null);

export default React.forwardRef(CollectionReportViewModal);
